/**
 * `SubscriptionPlan` → the wire. This is where the EX44 density policy stops
 * being arithmetic and becomes signalling.
 *
 * Rules, in order:
 *   1. A publisher absent from `plan.video` is UNSUBSCRIBED — not merely
 *      disabled. The SFU stops forwarding them entirely (study §3.2).
 *   2. A subscribed tile gets EXPLICIT dimensions from its role-clamped layer.
 *      LiveKit forwards `min(adaptive-stream demand, requested)`, so the role
 *      ceiling can never be exceeded — that is the 2x DPR trap, closed.
 *   3. Active speakers win the last subscription slots when the plan is bigger
 *      than the server-side backstop.
 * `setVideoQuality` is deliberately NOT used: its three buckets cannot express
 * the five-rung ladder, and setting it discards the exact dimensions above.
 */
import {
  LAYER_SPECS, MEDIA_LIMITS, QUALITY_PROFILES, type MediaLayer, type QualityProfile,
} from '@cyphex/types';
import { Track, type RemoteTrackPublication, type Room } from 'livekit-client';
import type { SubscriptionPlan } from './contract';
import { ceilingForRole, minLayer } from './densityPolicy';

export interface PlanContext {
  readonly profile: QualityProfile;
  /** Loudest first. Breaks ties when the plan exceeds the subscription cap. */
  readonly activeSpeakerIds: readonly string[];
  /** Tab hidden past the grace period — keep audio, stop every frame. */
  readonly videoPaused: boolean;
  /**
   * THE presenter, from `shareArbiter` — and now genuinely the only one, because
   * a losing share is stopped at its source rather than merely ignored here.
   */
  readonly screenShareOwnerId: string | null;
}

const speakerRank = (id: string, speakers: readonly string[]): number => {
  const index = speakers.indexOf(id);
  return index === -1 ? Number.MAX_SAFE_INTEGER : index;
};

/** Caps the plan at the server-side backstop, keeping the loudest publishers. */
export function rankedVideo(
  plan: SubscriptionPlan,
  ctx: PlanContext,
): ReadonlyMap<string, MediaLayer> {
  const entries = [...plan.video.entries()];
  if (entries.length > MEDIA_LIMITS.MAX_VIDEO_SUBSCRIPTIONS) {
    entries.sort(
      ([a], [b]) => speakerRank(a, ctx.activeSpeakerIds) - speakerRank(b, ctx.activeSpeakerIds),
    );
  }
  return new Map(entries.slice(0, MEDIA_LIMITS.MAX_VIDEO_SUBSCRIPTIONS));
}

/** Top-N audio. This is what turns O(N²) audio egress into O(3N) (study §4.1). */
export function rankedAudio(plan: SubscriptionPlan, ctx: PlanContext): ReadonlySet<string> {
  const limit = Math.min(
    QUALITY_PROFILES[ctx.profile].audioSubscriptions,
    MEDIA_LIMITS.MAX_AUDIO_SUBSCRIPTIONS,
  );
  const ids = [...plan.audio].sort(
    (a, b) => speakerRank(a, ctx.activeSpeakerIds) - speakerRank(b, ctx.activeSpeakerIds),
  );
  return new Set(ids.slice(0, limit));
}

function applyVideo(
  publication: RemoteTrackPublication | undefined,
  layer: MediaLayer | null,
  ctx: PlanContext,
): void {
  if (!publication) return;
  const wanted = layer !== null && QUALITY_PROFILES[ctx.profile].videoEnabled;
  if (publication.isDesired !== wanted) publication.setSubscribed(wanted);
  if (!wanted || layer === null) return;

  // Taking over `setEnabled` also takes over adaptive stream's own
  // pause-when-hidden: from here on, `visibilityWatcher` owns that decision.
  publication.setEnabled(!ctx.videoPaused);
  if (ctx.videoPaused) return;

  const spec = LAYER_SPECS[layer];
  publication.setVideoDimensions({ width: spec.width, height: spec.height });
  publication.setVideoFPS(spec.fps);
}

function applyAudio(publication: RemoteTrackPublication | undefined, wanted: boolean): void {
  if (!publication) return;
  if (publication.isDesired !== wanted) publication.setSubscribed(wanted);
}

/** Idempotent: every setter below no-ops when the value has not moved. */
export function applyPlan(room: Room, plan: SubscriptionPlan, ctx: PlanContext): void {
  const video = rankedVideo(plan, ctx);
  const audio = rankedAudio(plan, ctx);
  const profileCeiling = ceilingForRole('spotlight', ctx.profile);
  const shareLayer = plan.screenShare ? ceilingForRole('screen', ctx.profile) : null;

  for (const participant of room.remoteParticipants.values()) {
    const planned = video.get(participant.identity) ?? null;
    const presenting = participant.identity === ctx.screenShareOwnerId;
    const sharing = shareLayer !== null && presenting;

    applyVideo(
      participant.getTrackPublication(Track.Source.Camera),
      planned === null ? null : minLayer(planned, profileCeiling),
      ctx,
    );
    applyVideo(
      participant.getTrackPublication(Track.Source.ScreenShare),
      sharing ? shareLayer : null,
      ctx,
    );
    applyAudio(
      participant.getTrackPublication(Track.Source.Microphone),
      audio.has(participant.identity),
    );
    /* SHARE AUDIO FOLLOWS THE PRESENTER, NOT THE LAYOUT.
       `sharing` is false in gallery view, because `plan.screenShare` is — that
       is right for a 1.5 Mbps video track and wrong for a 48 kbps soundtrack.
       Gated on the layout, switching to Gallery during a demo would have
       silenced it, which is the same defect as never capturing it. */
    applyAudio(participant.getTrackPublication(Track.Source.ScreenShareAudio), presenting);
  }
}

export const EMPTY_PLAN: SubscriptionPlan = {
  video: new Map<string, MediaLayer>(),
  audio: [],
  screenShare: false,
};
